/**
 * Bonus Product System
 * Supports single and multi-category bonus products on PDP and quick view.
 */
(function() {
  function showValidationModal(title, message, onConfirm, allowDecline, onDecline, selectBtnText, declineBtnText) {
    const existingOverlay = document.querySelector('.bonus-modal-overlay');
    if (existingOverlay) existingOverlay.remove();

    const overlay = document.createElement('div');
    overlay.className = 'bonus-modal-overlay';

    const modal = document.createElement('div');
    modal.className = 'bonus-modal';

    const selectText = selectBtnText || 'Got it';

    modal.innerHTML = `
      <div class="bonus-modal-content">
        <h3>${title}</h3>
        <p>${message}</p>
        <div class="bonus-modal-buttons">
          <button class="bonus-btn-primary" id="confirmBtn">${selectText}</button>
        </div>
      </div>
    `;

    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    const confirmBtn = modal.querySelector('#confirmBtn');

    confirmBtn?.addEventListener('click', () => {
      overlay.remove();
      if (onConfirm) onConfirm();
    });

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        overlay.remove();
        if (onConfirm) onConfirm();
      }
    });

    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        overlay.remove();
        document.removeEventListener('keydown', handleEsc);
        if (onConfirm) onConfirm();
      }
    };
    document.addEventListener('keydown', handleEsc);
  }

  function findProductRoot(node) {
    return (
      node.closest('quick-view-modal') ||
      node.closest('product-info') ||
      node.closest('.product__info-wrapper') ||
      document.body
    );
  }

  function findForm(root) {
    // Must resolve to the real add-to-cart form, NOT the installment/payment_button
    // form. The theme renders <form class="installment" action="/cart/add"> before
    // the real product form, so a bare action*="/cart/add" match grabs the wrong one
    // and the add-to-cart interception silently binds to a form the customer never submits.
    return (
      root.querySelector('form[is="product-form"]') ||
      root.querySelector('form.main-product-form') ||
      root.querySelector('form[data-type="add-to-cart-form"]') ||
      root.querySelector('form[action*="/cart/add"]:not(.installment)') ||
      root.querySelector('form[data-product-form]')
    );
  }

  function findAddButton(root, form) {
    return (
      root.querySelector('[data-product-atc]') ||
      form?.querySelector('button[type="submit"][name="add"]') ||
      form?.querySelector('button[type="submit"]:not(.shopify-payment-button__button)')
    );
  }

  function findBuyNowButtons(root) {
    return root.querySelectorAll(
      '.shopify-payment-button button, ' +
      '.shopify-payment-button__button, ' +
      '[data-shopify="payment-button"] button, ' +
      '.product-form__submit--buy-now'
    );
  }

  function getContextGroups(root) {
    return [...new Set(Array.from(root.querySelectorAll('.bonus-block[data-group]')).map((el) => el.dataset.group))];
  }

  function getActiveBonusRoot() {
    const openQuickViewBonus = document.querySelector('quick-view-modal:not([hidden]) .bonus-block');
    if (openQuickViewBonus) {
      return findProductRoot(openQuickViewBonus);
    }

    const firstBonusBlock = document.querySelector('.bonus-block');
    return firstBonusBlock ? findProductRoot(firstBonusBlock) : null;
  }

  function getMainProductQuantity(root, form) {
    const scopedQuantityInput =
      root.querySelector('[data-quantity-input]') ||
      root.querySelector('quantity-selector input[type="number"]') ||
      form.querySelector('input[name="quantity"]') ||
      root.querySelector('.quantity-selector__input');

    if (scopedQuantityInput?.value) {
      const qty = parseInt(scopedQuantityInput.value, 10);
      if (qty > 0) return qty;
    }

    const formData = new FormData(form);
    const formQuantity = formData.get('quantity');
    if (formQuantity) {
      const qty = parseInt(formQuantity, 10);
      if (qty > 0) return qty;
    }

    return 1;
  }

  function openCartDrawer(root, triggerElement) {
    const cartDrawer = document.querySelector('cart-drawer');
    const quickViewModal = root.closest?.('quick-view-modal') || (root.tagName === 'QUICK-VIEW-MODAL' ? root : null);

    const refreshCart = () => {
      document.dispatchEvent(
        new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { open: true },
        })
      );

      if (!cartDrawer) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
        document.body.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
      }
    };

    if (quickViewModal?.hide) {
      quickViewModal.hide(true);
      setTimeout(refreshCart, 180);
      return;
    }

    if (cartDrawer && typeof cartDrawer.show === 'function') {
      refreshCart();
      setTimeout(() => cartDrawer.show(triggerElement), 60);
      return;
    }

    refreshCart();
  }

  /*
   * Pricing for the buy_x_get_y_percent_off rule type.
   * --------------------------------------------------
   * A buy_x_get_y bonus is free, so this card has always hard-coded R0.00.
   * buy_x_get_y_percent_off discounts the bonus instead of zeroing it.
   *
   * Gated on ruleType, NOT on the mere presence of percent_off. If a percentage
   * ends up on a free deal's product by mistake, the discount function still
   * zeroes that line — repricing the card off the stray number would advertise a
   * price the checkout will not charge.
   *
   * Every malformed value returns null, and null renders exactly what a free deal
   * renders. There is no partial-credit path: a bad percent can only ever produce
   * the old free price, never a wrong one. Matches the engine's valid_percent()
   * in buy-x-get-y-percent-off/src/cart_lines_discounts_generate_run.rs:86 —
   * finite, (0, 100].
   */
  var PERCENT_OFF_RULE = 'buy_x_get_y_percent_off';

  function getBonusPercentOff(group) {
    const system = (window.bonusProductSystem || {})[group] || {};
    if (system.ruleType !== PERCENT_OFF_RULE) return null;

    const raw = (system.config || {}).percent_off;
    // Number() alone is far too permissive: true and [25] both coerce to
    // plausible-looking percentages.
    if (typeof raw !== 'number' && typeof raw !== 'string') return null;

    const pct = Number(raw);
    if (!Number.isFinite(pct) || pct <= 0 || pct > 100) return null;
    return pct;
  }

  /*
   * Mirrors the engine cent-for-cent. It rounds the DISCOUNT and subtracts
   * (round_cents(unit_price * percent / 100), :219), which is NOT the same as
   * rounding the final price: at 25% off R129.90 the discount is 3247.5c -> 3248c
   * -> R97.42, where rounding the price gives R97.43. BBH prices end in .90 and
   * .99 constantly, so that one cent would be a routine mismatch between the card
   * and the checkout.
   */
  function bonusDealCents(cents, percentOff) {
    return cents - Math.round((cents * percentOff) / 100);
  }

  /*
   * Money for the percent card only; the free card keeps its literal so its output
   * cannot move. Falls back to the surrounding file's hardcoded-R style only if the
   * theme formatter is genuinely unusable — and checks moneyFormat is present,
   * because FoxTheme.Currency.formatMoney defaults to a DOLLAR format when called
   * without one (assets/theme.js:409), which would put $ on a ZAR storefront.
   */
  function formatBonusMoney(cents) {
    const fox = window.FoxTheme;
    const format = fox && fox.settings && fox.settings.moneyFormat;
    if (format && fox.Currency && typeof fox.Currency.formatMoney === 'function') {
      try {
        return fox.Currency.formatMoney(cents, format);
      } catch (err) {
        /* fall through to the literal below */
      }
    }
    return `R${(cents / 100).toFixed(2)}`;
  }

  async function renderBonusGrid(gridElement, bonusItems, group, categoryId, allowMultiple, isMultiCategory) {
    const products = [];
    const items = bonusItems.map((item) => (typeof item === 'string' ? { handle: item, variant_id: null } : item));

    for (const item of items) {
      if (!item.handle) continue;

      try {
        const res = await fetch(`/products/${item.handle}.js`);
        if (!res.ok) throw new Error(`404 for ${item.handle}`);
        const data = await res.json();

        if (item.variant_id) {
          const specificVariant = data.variants.find((variant) => variant.id === item.variant_id);
          if (specificVariant) data.variants = [specificVariant];
        }

        // Out-of-stock bonus products must not be offered. Nothing filtered on
        // availability before this: the only stock check was the `disabled`
        // attribute on individual variant radios, which is emitted ONLY for
        // multi-variant products. Every Snugfit Rug Stabiliser is single-variant,
        // so two sold-out bonuses rendered as fully selectable (Joshua, 16 Sep).
        // Filtering the variants here also means a multi-variant bonus offers
        // only the sizes actually in stock.
        data.variants = data.variants.filter((variant) => variant.available);
        if (!data.variants.length) continue;

        products.push(data);
      } catch (err) {
        console.warn('Could not load bonus product:', item.handle);
      }
    }

    if (!products.length) {
      gridElement.innerHTML = '';
      return 0;
    }

    let inputType;
    let inputName;

    if (isMultiCategory) {
      inputType = 'radio';
      inputName = allowMultiple ? `bonus_product_${group}_${categoryId}` : `bonus_product_${group}`;
    } else {
      inputType = allowMultiple ? 'checkbox' : 'radio';
      inputName = `bonus_product_${group}`;
    }

    // Per GROUP, not per product: the percentage lives on the base product's
    // ui_config, so it is the same for every card in this grid.
    const percentOff = getBonusPercentOff(group);

    gridElement.innerHTML = products
      .map((product) => {
        const maxPrice = Math.max(...product.variants.map((variant) => variant.price));

        /*
         * Free deals keep quoting maxPrice, byte for byte — on a free card the
         * struck figure is decoration beside an unconditional R0.00.
         * A percent card is different: the number IS the offer, and the variant
         * radios below let the shopper switch to a cheaper size. Quote the LOWEST
         * available variant so the card never advertises less off than the shopper
         * gets, and mark it "From" when the sizes actually differ in price. The
         * change handler further down retunes it to the chosen variant.
         */
        const minPrice = Math.min(...product.variants.map((variant) => variant.price));
        const priceVaries = minPrice !== maxPrice;
        const basePrice = percentOff === null ? maxPrice : minPrice;
        const dealCents = percentOff === null ? 0 : bonusDealCents(basePrice, percentOff);
        const originalText = percentOff === null ? `R${(maxPrice / 100).toFixed(2)}` : formatBonusMoney(basePrice);
        const dealText =
          percentOff === null
            ? 'R0.00'
            : `${priceVaries ? 'From ' : ''}${formatBonusMoney(dealCents)}`;
        const dealClass = percentOff === null ? 'free-price' : 'free-price free-price--percent';
        const hasMultipleVariants = product.variants.length > 1;
        const singleVariantId = !hasMultipleVariants ? product.variants[0].id : null;

        return `
          <div class="bonus-product" data-group="${group}" data-category="${categoryId || ''}" data-handle="${product.handle}" data-single-variant="${singleVariantId || ''}">
            <label class="bonus-main">
              <input type="${inputType}" name="${inputName}" value="${product.handle}" data-single-variant-id="${singleVariantId || ''}">
              <div class="bonus-card">
                <img src="${product.images[0]}" alt="${product.title}" class="bonus-image"/>
                <div class="bonus-details">
                  <h4>${product.title}</h4>
                  <div class="bonus-price">
                    <span class="original-price">${originalText}</span>
                    <span class="${dealClass}">${dealText}</span>
                  </div>
                </div>
              </div>
            </label>
            ${
              hasMultipleVariants
                ? `
                  <div class="variant-options" style="display:none;">
                    ${product.variants
                      .map(
                        (variant) => `
                          <label class="variant-option ${variant.available ? '' : 'disabled'}">
                            <input type="radio" name="variant_${group}_${categoryId ? `${categoryId}_` : ''}${product.handle}" value="${variant.id}" ${
                              !variant.available ? 'disabled' : ''
                            }>
                            <span>${variant.title}</span>
                          </label>
                        `
                      )
                      .join('')}
                  </div>
                `
                : ''
            }
          </div>
        `;
      })
      .join('');

    /*
     * Percent cards only: retune the quoted price to the variant the shopper picks.
     * Without this a multi-size bonus keeps showing the "From" price after they
     * choose the expensive size, which is a number the checkout will not charge.
     * Free cards are never bound — R0.00 is the same for every variant — so this
     * loop is a no-op for every deal running today.
     */
    if (percentOff !== null) {
      gridElement.querySelectorAll('.variant-options input[type="radio"]').forEach((variantInput) => {
        variantInput.addEventListener('change', (event) => {
          const card = event.target.closest('.bonus-product');
          const priceEl = card && card.querySelector('.free-price--percent');
          const handle = card && card.dataset.handle;
          const product = products.find((candidate) => candidate.handle === handle);
          if (!priceEl || !product) return;

          const chosen = product.variants.find((variant) => String(variant.id) === event.target.value);
          if (!chosen) return;

          priceEl.textContent = formatBonusMoney(bonusDealCents(chosen.price, percentOff));
          const originalEl = card.querySelector('.original-price');
          if (originalEl) originalEl.textContent = formatBonusMoney(chosen.price);
        });
      });
    }

    gridElement.querySelectorAll(`input[name="${inputName}"]`).forEach((input) => {
      input.addEventListener('change', (event) => {
        const parent = event.target.closest('.bonus-product');
        const variantOptions = parent.querySelector('.variant-options');

        if (variantOptions) {
          if (event.target.checked) {
            variantOptions.style.display = 'flex';
          } else {
            variantOptions.style.display = 'none';
            variantOptions.querySelectorAll('input[type="radio"]').forEach((radio) => {
              radio.checked = false;
            });
          }
        }

        if (inputType === 'radio') {
          const wrapper = event.target.closest('.bonus-block');
          wrapper?.querySelectorAll('.variant-options').forEach((options) => {
            if (options !== variantOptions) {
              options.style.display = 'none';
              options.querySelectorAll('input[type="radio"]').forEach((radio) => {
                radio.checked = false;
              });
            }
          });
        }
      });
    });

    return products.length;
  }

  async function initBonusContext(root) {
    const groups = getContextGroups(root);
    if (!groups.length) return;

    const form = findForm(root);
    if (!form) {
      console.warn('Bonus Product System: Could not find product form for context', root);
      return;
    }

    const groupsKey = groups.join('|');
    if (root._bonusActiveForm === form && root._bonusGroupsKey === groupsKey) {
      return;
    }

    if (root._bonusObserver) {
      root._bonusObserver.disconnect();
    }

    root._bonusActiveForm = form;
    root._bonusGroupsKey = groupsKey;

    const addBtn = findAddButton(root, form);
    let userDeclinedBonus = false;
    let userPromptedToSelect = false;
    let isProcessing = false;

    async function renderBonusProducts(group) {
      const bonusSystem = window.bonusProductSystem[group];
      if (!bonusSystem) return;

      let rendered = 0;

      if (bonusSystem.isMultiCategory) {
        const allowMultiple = bonusSystem.config.allow_multiple;
        for (const category of bonusSystem.categories) {
          const grid = root.querySelector(`#bonus-grid-${group}-${category.id}`);
          const bonusItems = category.bonus_variants || category.bonus_handles;
          if (grid && bonusItems && !grid.dataset.initialized) {
            const count = await renderBonusGrid(grid, bonusItems, group, category.id, allowMultiple, true);
            grid.dataset.initialized = 'true';
            rendered += count;
            // Hide a category whose own bonuses are all sold out, so the remaining
            // categories still read cleanly instead of showing an empty heading.
            if (!count) {
              const categoryEl = grid.closest('.bonus-category');
              if (categoryEl) categoryEl.hidden = true;
            }
          } else if (grid && grid.dataset.initialized) {
            rendered += grid.querySelectorAll('.bonus-product').length;
          }
        }
      } else {
        const grid = root.querySelector(`#bonus-grid-${group}`);
        const bonusItems = bonusSystem.variants || bonusSystem.handles;
        if (grid && bonusItems && !grid.dataset.initialized) {
          rendered = await renderBonusGrid(grid, bonusItems, group, null, bonusSystem.config.allow_multiple, false);
          grid.dataset.initialized = 'true';
        } else if (grid) {
          rendered = grid.querySelectorAll('.bonus-product').length;
        }
      }

      // Nothing left in stock -> hide the whole block. Previously it stayed up with
      // sold-out options a shopper could pick, and the add would then fail. The block
      // is rendered by Liquid before stock is known (availability comes from the
      // per-product fetches above), so hiding here is the only place this can happen.
      const wrapper = root.querySelector(`#bonus-wrapper-${group}`);
      if (wrapper) wrapper.hidden = !rendered;

      return rendered;
    }

    function getMainProductVariantId() {
      const variantSelect = form.querySelector('select[name="id"]');
      const variantRadio = form.querySelector('input[name="id"]:checked');
      const variantInput = form.querySelector('input[name="id"]');

      if (variantSelect?.value) return variantSelect.value;
      if (variantRadio?.value) return variantRadio.value;
      if (variantInput?.value) return variantInput.value;
      return null;
    }

    function getSelectedBonusProducts(group) {
      const bonusSystem = window.bonusProductSystem[group];
      const selectedProducts = [];
      const allowMultiple = bonusSystem.config.allow_multiple;

      if (bonusSystem.isMultiCategory) {
        if (allowMultiple) {
          for (const category of bonusSystem.categories) {
            const selected = root.querySelector(`input[name="bonus_product_${group}_${category.id}"]:checked`);
            if (selected) {
              selectedProducts.push({
                input: selected,
                categoryId: category.id,
                categoryTitle: category.title,
              });
            }
          }
        } else {
          const selected = root.querySelector(`input[name="bonus_product_${group}"]:checked`);
          if (selected) {
            const productElement = selected.closest('.bonus-product');
            const categoryId = productElement ? productElement.dataset.category : null;
            const category = bonusSystem.categories.find((item) => item.id === categoryId);
            selectedProducts.push({
              input: selected,
              categoryId,
              categoryTitle: category ? category.title : null,
            });
          }
        }
      } else {
        root.querySelectorAll(`input[name="bonus_product_${group}"]:checked`).forEach((input) => {
          selectedProducts.push({
            input,
            categoryId: null,
            categoryTitle: null,
          });
        });
      }

      return selectedProducts;
    }

    function validateCategorySelections(group) {
      const bonusSystem = window.bonusProductSystem[group];
      if (!bonusSystem.isMultiCategory) {
        return { valid: true, missingCategories: [], isOrMode: false };
      }

      const allowMultiple = bonusSystem.config.allow_multiple;
      if (!allowMultiple) {
        return { valid: true, missingCategories: [], isOrMode: true };
      }

      const missingCategories = [];
      for (const category of bonusSystem.categories) {
        const selected = root.querySelector(`input[name="bonus_product_${group}_${category.id}"]:checked`);
        if (!selected) missingCategories.push(category);
      }

      return {
        valid: missingCategories.length === 0,
        missingCategories,
        isOrMode: false,
      };
    }

    function validateBonusSelection(isBuyItNow = false) {
      if (userDeclinedBonus) return null;

      for (const group of groups) {
        const bonusSystem = window.bonusProductSystem[group];
        const selectedProducts = getSelectedBonusProducts(group);

        for (const { input, categoryId } of selectedProducts) {
          const singleVariantId = input.dataset.singleVariantId;
          if (singleVariantId) continue;

          const variantName = categoryId
            ? `variant_${group}_${categoryId}_${input.value}`
            : `variant_${group}_${input.value}`;
          const selectedVariant = root.querySelector(`input[name="${variantName}"]:checked`);

          if (!selectedVariant) {
            const promptConfig = bonusSystem.promptConfig || {};
            return {
              title: 'Please select a size for your bonus product',
              message: "You've chosen a bonus product but haven't selected a size yet. Select a size or continue without the bonus.",
              selectBtn: 'Select Size',
              declineBtn: promptConfig.declineBtn || 'Skip bonus, continue',
              scrollTo: () => {
                const productElement = input.closest('.bonus-product');
                if (productElement) {
                  productElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  const variantOpts = productElement.querySelector('.variant-options');
                  if (variantOpts) variantOpts.style.animation = 'pulse 0.5s ease-in-out 2';
                }
              },
              allowDecline: true,
            };
          }
        }

        if (!selectedProducts.length) {
          const promptConfig = bonusSystem.promptConfig || {};
          const wrapper = root.querySelector(`#bonus-wrapper-${group}`) || root.querySelector(`.bonus-block[data-group="${group}"]`);
          return {
            title: isBuyItNow ? promptConfig.title || 'You have a bonus waiting!' : promptConfig.title || "Don't forget your bonus!",
            message: isBuyItNow
              ? 'This product comes with a bonus item. Select your bonus below, or continue without it.'
              : promptConfig.message || bonusSystem.config.description || 'Select your bonus product to add it with your order.',
            selectBtn: promptConfig.selectBtn || 'Select Now',
            declineBtn: promptConfig.declineBtn || 'No thanks, continue',
            scrollTo: () => {
              if (wrapper) {
                wrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
                wrapper.style.animation = 'pulse 0.5s ease-in-out 2';
              }
            },
            allowDecline: true,
          };
        }

        if (bonusSystem.isMultiCategory) {
          const validation = validateCategorySelections(group);
          if (!validation.valid) {
            const promptConfig = bonusSystem.promptConfig || {};
            const missingTitles = validation.missingCategories.map((category) => category.title || category.id).join(', ');
            return {
              title: 'Complete your bonus selection',
              message: `You still need to select: ${missingTitles}. Complete your selection or continue without bonuses.`,
              selectBtn: 'Complete Selection',
              declineBtn: promptConfig.declineBtn || 'Skip bonuses, continue',
              scrollTo: () => {
                const firstMissing = validation.missingCategories[0];
                const grid = root.querySelector(`#bonus-grid-${group}-${firstMissing.id}`);
                if (grid) {
                  grid.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  grid.style.animation = 'pulse 0.5s ease-in-out 2';
                }
              },
              allowDecline: true,
            };
          }
        }
      }

      return null;
    }

    async function addToCart() {
      if (isProcessing) return;
      isProcessing = true;

      if (addBtn) {
        addBtn.disabled = true;
        addBtn.dataset.originalText = addBtn.textContent;
        addBtn.textContent = 'Adding...';
      }

      try {
        const formData = new FormData(form);
        const mainResponse = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
        });

        if (!mainResponse.ok) {
          const data = await mainResponse.json();
          throw new Error(data.description || 'Failed to add main product to cart');
        }

        // Every other add-to-cart path in the theme goes through ProductForm, which
        // dispatches this (assets/theme.js:2765). This path intercepts the submit and
        // fetches directly, so without it any analytics listening for an add would miss
        // every bonus-deal product. The body posted above is a plain FormData — single
        // id/quantity, no items[], no sections — so the response is the flat line-item
        // shape, byte-for-byte compatible with the primary path. Bonus gift lines below
        // deliberately do NOT dispatch: the shopper didn't choose them.
        // Own try/catch so a misbehaving listener can never surface the
        // "Unable to add to cart" modal in the catch block below.
        try {
          const mainLine = await mainResponse.json();
          document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: mainLine } }));
        } catch (dispatchError) {}

        if (!userDeclinedBonus) {
          for (const group of groups) {
            const selectedProducts = getSelectedBonusProducts(group);

            for (const { input, categoryId } of selectedProducts) {
              const singleVariantId = input.dataset.singleVariantId;
              let variantId = singleVariantId;

              if (!variantId) {
                const variantName = categoryId
                  ? `variant_${group}_${categoryId}_${input.value}`
                  : `variant_${group}_${input.value}`;
                variantId = root.querySelector(`input[name="${variantName}"]:checked`)?.value || null;
              }

              if (!variantId) continue;

              await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  id: parseInt(variantId, 10),
                  quantity: getMainProductQuantity(root, form),
                  properties: {
                    _bonus_group: group,
                    ...(categoryId && { _bonus_category: categoryId }),
                  },
                }),
              });
            }
          }
        }

        openCartDrawer(root, addBtn || form.querySelector('button[type="submit"]'));
      } catch (err) {
        console.error('Error:', err);
        showValidationModal('Unable to add to cart', err.message || 'Please try again.', null, false);
      } finally {
        if (addBtn) {
          addBtn.disabled = false;
          addBtn.textContent = addBtn.dataset.originalText || 'Add to Cart';
        }
        isProcessing = false;
      }
    }

    function handleAddToCart(event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (!getMainProductVariantId()) {
        showValidationModal('Please select a size', 'You need to select a size for the product before adding to cart.', null, false);
        return false;
      }

      const validationError = validateBonusSelection(false);
      if (validationError) {
        showValidationModal(
          validationError.title,
          validationError.message,
          () => {
            userPromptedToSelect = true;
            validationError.scrollTo?.();
          },
          validationError.allowDecline,
          () => {
            userDeclinedBonus = true;
            addToCart();
          },
          validationError.selectBtn,
          validationError.declineBtn
        );
        return false;
      }

      addToCart();
      return false;
    }

    function handleBuyItNow(event) {
      const validationError = validateBonusSelection(true);
      if (!validationError) return true;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const clickedButton = event.target.closest('button') || event.target;
      showValidationModal(
        validationError.title,
        validationError.message,
        () => {
          userPromptedToSelect = true;
          validationError.scrollTo?.();
        },
        validationError.allowDecline,
        () => {
          userDeclinedBonus = true;
          clickedButton?.click();
        },
        validationError.selectBtn,
        validationError.declineBtn
      );

      return false;
    }

    groups.forEach((group) => {
      renderBonusProducts(group);
    });

    if (!form._bonusSubmitBound) {
      form.addEventListener('submit', handleAddToCart, true);
      form._bonusSubmitBound = true;
    }

    if (addBtn && !addBtn._bonusClickBound) {
      addBtn.addEventListener(
        'click',
        (event) => {
          if (addBtn.type === 'submit' || addBtn.getAttribute('type') === 'submit') {
            handleAddToCart(event);
          }
        },
        true
      );
      addBtn._bonusClickBound = true;
    }

    const bindBuyNowButtons = (scope) => {
      findBuyNowButtons(scope).forEach((button) => {
        if (button._bonusBuyNowBound) return;
        button.addEventListener('click', handleBuyItNow, true);
        button._bonusBuyNowBound = true;
      });
    };

    bindBuyNowButtons(root);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;

          bindBuyNowButtons(node);

          if (node.matches?.('.bonus-block') || node.querySelector?.('.bonus-block')) {
            const nextRoot = findProductRoot(node.matches?.('.bonus-block') ? node : node.querySelector('.bonus-block'));
            initBonusContext(nextRoot);
          }
        });
      });
    });

    observer.observe(root, { childList: true, subtree: true });
    root._bonusObserver = observer;

    root._bonusApi = {
      validateBonusSelection,
      showBonusValidationModal: showValidationModal,
      bonusUserDeclined: () => userDeclinedBonus,
      setBonusUserDeclined: (value) => {
        userDeclinedBonus = value;
      },
      bonusUserPrompted: () => userPromptedToSelect,
    };
  }

  function initBonusSystem() {
    if (!window.bonusProductSystem) return;

    document.querySelectorAll('.bonus-block').forEach((bonusBlock) => {
      initBonusContext(findProductRoot(bonusBlock));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBonusSystem);
  } else {
    initBonusSystem();
  }

  window.validateBonusSelection = function(isBuyItNow = false) {
    const activeRoot = getActiveBonusRoot();
    return activeRoot?._bonusApi?.validateBonusSelection
      ? activeRoot._bonusApi.validateBonusSelection(isBuyItNow)
      : null;
  };

  window.showBonusValidationModal = showValidationModal;

  window.bonusUserDeclined = function() {
    const activeRoot = getActiveBonusRoot();
    return activeRoot?._bonusApi?.bonusUserDeclined ? activeRoot._bonusApi.bonusUserDeclined() : false;
  };

  window.setBonusUserDeclined = function(value) {
    const activeRoot = getActiveBonusRoot();
    if (activeRoot?._bonusApi?.setBonusUserDeclined) {
      activeRoot._bonusApi.setBonusUserDeclined(value);
    }
  };

  window.bonusUserPrompted = function() {
    const activeRoot = getActiveBonusRoot();
    return activeRoot?._bonusApi?.bonusUserPrompted ? activeRoot._bonusApi.bonusUserPrompted() : false;
  };
})();
